<script setup lang="ts">
import { ref, onMounted } from 'vue';
import sanitizeHtml from 'sanitize-html';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';

const isMobile = ref(false);

export interface ServiceItem {
  image?: {
    id: string;
    url: string;
    width?: number;
    height?: number;
    alt?: string;
    name?: string;
  };
  label?: string;
  title?: string;
  text?: string;
  image_cover?: boolean;
  button?: {
    url: string;
    title: string;
    target: string;
  };
}

defineProps<{
  section: {
    heading?: string;
    text?: string;
    button?: {
      url: string;
      title: string;
      target: string;
    };
    services?: ServiceItem[];
  };
}>();

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});
</script>

<template>
  <section class="relative z-10 bg-grad-cream pb-16 pt-15 md:py-28 lg:pb-28">
    <div class="container lg:pt-4">
      <h2
        v-if="section.heading"
        class="h3 text-center"
        :class="
          section.text || section.button ? 'mb-1 md:mb-4' : 'mb-8 md:mb-20'
        "
      >
        {{ section.heading }}
      </h2>
      <div
        v-if="section.text || section.button"
        class="mx-auto mb-20 max-w-4xl text-center md:mb-30"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="section.text"
          class="mb-7 text-xl last:mb-0 md:mb-8 md:last:mb-0"
          v-html="sanitizeHtml(section.text)"
        />
        <ButtonLink
          v-if="section.button"
          v-bind="section.button"
          icon="arrow-r"
        />
      </div>
      <ClientOnly>
        <!-- Mobile Swiper Slider -->
        <Swiper
          v-if="isMobile && section?.services?.length"
          class="!mx-0 min-w-[300px] max-w-[86%] !overflow-visible"
          :slides-per-view="1"
          :pagination="{ clickable: true }"
          :space-between="0"
          :navigation="true"
          :modules="[Navigation, Pagination]"
          :speed="800"
        >
          <template
            v-for="(serviceItem, index) in section.services"
            :key="`${serviceItem.title}-${index}`"
          >
            <SwiperSlide
              v-if="
                serviceItem?.image?.url ||
                serviceItem.image ||
                serviceItem.label ||
                serviceItem.text ||
                serviceItem.button
              "
            >
              <template v-if="serviceItem.image_cover">
                <svg
                  v-if="serviceItem?.image?.url"
                  class="relative z-10 mb-9 aspect-square h-auto w-full"
                  width="440"
                  height="440"
                  fill="none"
                  viewBox="0 0 304 298"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink"
                >
                  <clipPath :id="`clip-path-${index}`" :key="`clip-${index}`">
                    <path
                      v-if="index % 3 === 1"
                      d="M98.1012 292.053C79.3391 287.085 59.5791 280.633 43.3902 269.686C14.8389 250.363 3.96922 213.318 0.810601 180.685C-5.12728 119.283 21.7087 49.8035 76.3372 18.0078C139.724 -18.8799 221.791 3.76027 265.78 59.9376C289.045 89.6435 300.327 119.515 302.282 157.216C303.14 173.735 304.022 189.177 299.857 205.291C295.016 224.025 286.027 234.449 272.279 247.768C247.901 271.378 216.636 287.11 183.862 295.76C174.189 298.314 164.078 298.206 154.123 297.734C151.394 297.601 110.967 295.453 98.1012 292.053Z"
                    />
                    <path
                      v-else-if="index % 3 === 2"
                      d="M164.171 0.693711C127.03 -2.11816 72.6477 2.2448 50.6601 39.7696C28.8994 76.9211 7.27154 117.1 0.961468 161.319C-5.13749 204.069 18.4764 229.085 50.4178 250.726C84.4312 273.768 121.205 292.489 161.716 296.545C195.847 299.971 221.877 298.578 250.894 278.256C277.229 259.809 287.73 228.571 296.604 197.807C316.536 128.762 279.035 56.4998 220.649 24.1674C208.686 17.54 200.108 9.63529 187.136 5.6207C182.601 4.21892 171.396 1.24115 164.171 0.693711Z"
                    />
                    <path
                      v-else
                      d="M170.791 1.01753C149 -0.477125 131.511 -1.08754 110.507 4.71758C81.1671 12.8221 52.804 23.566 31.7922 46.5112C15.4858 64.3314 4.13735 97.7533 0.970517 121.532C-3.62912 156.104 8.50904 196.214 29.1545 224.373C59.8215 266.215 108.57 285.023 158.164 295.2C185.021 300.714 211.52 298.05 236.83 287.509C264.965 275.793 275.239 257.641 284.17 229.627C293.915 199.064 302.748 165.617 303.961 133.491C305.092 103.356 281.597 70.9299 260.561 50.7861C242.822 33.7918 221.827 19.0563 199.219 9.69687C190.923 6.264 179.73 2.03767 170.791 1.02562V1.01753Z"
                    />
                  </clipPath>
                  <image
                    x="0"
                    y="0"
                    width="440"
                    height="440"
                    class="h-full w-full object-cover"
                    preserveAspectRatio="xMidYMid slice"
                    :xlink:href="serviceItem?.image?.url"
                    :clip-path="`url(#clip-path-${index})`"
                  />
                </svg>
              </template>
              <template v-else>
                <div
                  v-if="serviceItem.image"
                  class="relative z-10 mb-9 flex aspect-[1/0.984] items-center justify-center"
                >
                  <svg
                    class="absolute left-0 top-0 -z-10 h-full w-full text-white"
                  >
                    <use :href="`/icons/icons.svg#shape-${(index % 3) + 1}`" />
                  </svg>
                  <Image
                    v-if="serviceItem.image"
                    class="h-auto max-w-[72%]"
                    :data="serviceItem.image"
                  />
                </div>
              </template>
              <strong v-if="serviceItem.label" class="label mb-5 block">
                {{ serviceItem.label }}
              </strong>
              <h3 v-if="serviceItem.title" class="title mb-3">
                {{ serviceItem.title }}
              </h3>
              <div
                v-if="serviceItem.text"
                class="mb-4 last:mb-0"
                v-html="sanitizeHtml(serviceItem.text)"
              />
              <ButtonLink
                v-if="serviceItem.button"
                v-bind="serviceItem.button"
                icon="arrow-r"
              />
            </SwiperSlide>
          </template>
        </Swiper>
      </ClientOnly>
      <ol
        v-if="!isMobile"
        class="grid grid-cols-1 gap-10 md:grid-cols-3 lg:gap-x-20 xl:gap-x-[155px] xl:px-18"
      >
        <template
          v-for="(serviceItem, index) in section.services"
          :key="`${serviceItem.title}-${serviceItem?.image?.id}`"
        >
          <li
            v-if="
              serviceItem?.image?.url ||
              serviceItem.image ||
              serviceItem.label ||
              serviceItem.text ||
              serviceItem.button
            "
          >
            <template v-if="serviceItem?.image_cover">
              <svg
                v-if="serviceItem?.image?.url"
                class="relative z-10 mb-9 aspect-square h-auto w-full"
                width="440"
                height="440"
                fill="none"
                viewBox="0 0 304 298"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
              >
                <clipPath :id="`clip-path-${index}`" :key="`clip-${index}`">
                  <path
                    v-if="index % 3 === 1"
                    d="M98.1012 292.053C79.3391 287.085 59.5791 280.633 43.3902 269.686C14.8389 250.363 3.96922 213.318 0.810601 180.685C-5.12728 119.283 21.7087 49.8035 76.3372 18.0078C139.724 -18.8799 221.791 3.76027 265.78 59.9376C289.045 89.6435 300.327 119.515 302.282 157.216C303.14 173.735 304.022 189.177 299.857 205.291C295.016 224.025 286.027 234.449 272.279 247.768C247.901 271.378 216.636 287.11 183.862 295.76C174.189 298.314 164.078 298.206 154.123 297.734C151.394 297.601 110.967 295.453 98.1012 292.053Z"
                  />
                  <path
                    v-else-if="index % 3 === 2"
                    d="M164.171 0.693711C127.03 -2.11816 72.6477 2.2448 50.6601 39.7696C28.8994 76.9211 7.27154 117.1 0.961468 161.319C-5.13749 204.069 18.4764 229.085 50.4178 250.726C84.4312 273.768 121.205 292.489 161.716 296.545C195.847 299.971 221.877 298.578 250.894 278.256C277.229 259.809 287.73 228.571 296.604 197.807C316.536 128.762 279.035 56.4998 220.649 24.1674C208.686 17.54 200.108 9.63529 187.136 5.6207C182.601 4.21892 171.396 1.24115 164.171 0.693711Z"
                  />
                  <path
                    v-else
                    d="M170.791 1.01753C149 -0.477125 131.511 -1.08754 110.507 4.71758C81.1671 12.8221 52.804 23.566 31.7922 46.5112C15.4858 64.3314 4.13735 97.7533 0.970517 121.532C-3.62912 156.104 8.50904 196.214 29.1545 224.373C59.8215 266.215 108.57 285.023 158.164 295.2C185.021 300.714 211.52 298.05 236.83 287.509C264.965 275.793 275.239 257.641 284.17 229.627C293.915 199.064 302.748 165.617 303.961 133.491C305.092 103.356 281.597 70.9299 260.561 50.7861C242.822 33.7918 221.827 19.0563 199.219 9.69687C190.923 6.264 179.73 2.03767 170.791 1.02562V1.01753Z"
                  />
                </clipPath>
                <image
                  x="0"
                  y="0"
                  width="440"
                  height="440"
                  class="h-full w-full object-cover"
                  preserveAspectRatio="xMidYMid slice"
                  :xlink:href="serviceItem?.image?.url"
                  :clip-path="`url(#clip-path-${index})`"
                />
              </svg>
            </template>
            <template v-else>
              <div
                v-if="serviceItem.image"
                class="relative z-10 mb-9 flex aspect-[1/0.984] items-center justify-center"
              >
                <svg
                  class="absolute left-0 top-0 -z-10 h-full w-full text-white"
                >
                  <use :href="`/icons/icons.svg#shape-${(index % 3) + 1}`" />
                </svg>
                <Image
                  v-if="serviceItem.image"
                  class="h-auto max-w-[72%]"
                  :data="serviceItem.image"
                />
              </div>
            </template>
            <strong v-if="serviceItem.label" class="label mb-5 block">
              {{ serviceItem.label }}
            </strong>
            <h3 v-if="serviceItem.title" class="title mb-4">
              {{ serviceItem.title }}
            </h3>
            <div
              v-if="serviceItem.text"
              class="mb-4 last:mb-0"
              v-html="sanitizeHtml(serviceItem.text)"
            />
            <ButtonLink
              v-if="serviceItem.button"
              v-bind="serviceItem.button"
              icon="arrow-r"
            />
          </li>
        </template>
      </ol>
    </div>
  </section>
</template>
